<!--
 This component provides a navigation drawer, in which one can
 change the active operation from within the site view.
-->

<script setup lang="ts">
import { ref, watch } from "vue";
import { useUiStore } from "@/stores/ui";
import { useTerminalsStore } from "@/stores/terminals";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useTerminalOperations } from "@/composables/terminalOperations";
import { terminalOperation } from "@/models/ui/operation";
import DischargeMenu from "./DischargeMenu.vue";
import TankToTankMenu from "./TankToTankMenu.vue";
import TerminalDataMenu from "./TerminalDataMenu.vue";

const router = useRouter();
const route = useRoute();

const uiStore = useUiStore();
const terminalsStore = useTerminalsStore();

const { terminalOperations: operations } = useTerminalOperations();

const { rail, drawer, terminalOperationId } = storeToRefs(uiStore);

const { activeTerminal } = storeToRefs(terminalsStore);

// This will be null if the navigationdrawer is not set
const activeOperation = ref<terminalOperation | null>(
  terminalOperationId.value !== null
    ? operations[terminalOperationId.value]
    : null,
);

function navigateToOperationPage(operation: terminalOperation) {
  if (terminalOperationId.value === null) return;
  terminalOperationId.value = operation.value;
  activeOperation.value = operation;

  router.push({
    name: operations[operation.value].name,
    params: {
      terminalId: activeTerminal.value?.id,
    },
  });
}

/**
 * Set the operation id based on the current route.
 */
function setOperationFromRouter() {
  for (const route of router.currentRoute.value.matched) {
    // See if this route has any matching operations
    const matchingOperations = operations.filter(
      (operation) => operation.title == route.meta.title,
    );

    if (matchingOperations.length > 0) {
      terminalOperationId.value = matchingOperations[0].value;
      localStorage.setItem(
        "lastUsedOperationId",
        `${terminalOperationId.value}`,
      );
      activeOperation.value = matchingOperations[0];
      return;
    }
  }
}
watch(
  route,
  () => {
    setOperationFromRouter();
  },
  { immediate: true },
);

watch(activeOperation, (newActiveOperation, currentOperation) => {
  // Only apply this if this is not the first time the operation is being set. Prevents getting redirected
  // when you shouldn't be.
  if (currentOperation?.value != null && newActiveOperation != null) {
    navigateToOperationPage(newActiveOperation);
  }
});
</script>

<template>
  <v-navigation-drawer
    v-if="activeTerminal && terminalOperationId !== null"
    v-model="drawer"
    class="navigation-drawer menu-font scoll-bar"
    :rail="rail"
    rail-width="70"
    permanent
    dark
    width="280"
    @click="rail = false"
  >
    <v-container v-if="!rail" :elevation="2">
      <v-row no-gutters>
        <v-col id="operation-select-container" cols="12">
          <v-select
            id="operation-select"
            :eager="true"
            v-model="activeOperation"
            bg-color="rgba(255, 255, 255, 0.2)"
            item-color="orange lighten-1"
            rounded="rounded-lg"
            :items="operations.filter((operation) => operation.enabled)"
            style="font-size: 16px"
            base-color="orange lighten-1"
            :menu-props="{ openOnHover: true, openDelay: 1 }"
            variant="solo"
            auto
            :return-object="true"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-divider />

    <div v-if="terminalOperationId == 0">
      <DischargeMenu />
    </div>

    <div v-if="terminalOperationId == 1">
      <TankToTankMenu />
    </div>

    <div v-if="terminalOperationId == 3">
      <TerminalDataMenu />
    </div>
    <v-spacer />
    <template #append>
      <v-layout align-end justify-end class="navigation-drawer pa-2">
        <v-spacer />
        <v-btn
          v-if="rail"
          icon
          color="white"
          dark
          left
          size="large"
          variant="text"
          @click.stop="rail = !rail"
        >
          <v-icon id="chevron_right" variant="text" color="white">
            chevron_right
          </v-icon>
        </v-btn>
        <v-btn
          v-if="!rail"
          icon
          color="white"
          dark
          left
          size="large"
          variant="text"
          @click.stop="rail = !rail"
        >
          <v-icon id="chevron_left" color="white" size="x-large">
            chevron_left
          </v-icon>
        </v-btn>
      </v-layout>
    </template>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
$error-color: #ff2424;

:deep(.v-select__selection),
:deep(.v-select__selection--comma),
:deep(.v-select.v-text-field) input {
  color: white !important;
}

.v-navigation-drawer__content {
  height: 100%;
  background-color: #242d40;
}

.navigation-drawer {
  background-color: #242d40;
}

.menu-font {
  font-family: "Montserrat", sans-serif;
  font-size: 14px;

  .menu-header-item {
    font-weight: bolder;
  }

  .menu-item {
    font-weight: lighter;
  }
}

.v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 12px;
}

.v-application--is-ltr
  .v-list-group--no-action
  > .v-list-group__items
  > .v-list-item {
  padding-left: 52px;
}

.scoll-bar {
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: #242d40;
  }

  ::-webkit-scrollbar-thumb {
    background: #acabaa;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #929190;
  }

  *:hover::-webkit-scrollbar {
    width: 14px;
  }
}

.v-navigation-drawer--rail {
  :deep(.v-list-group__items) {
    position: absolute !important;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);
    white-space: nowrap;
    display: initial;
  }

  :deep(.v-list-item__spacer),
  :deep(.v-list-item__overlay),
  :deep(.v-list-item__content),
  :deep(.v-list-item__append) {
    display: none;
  }

  :deep(.v-list-item__prepend) {
    justify-content: center;
    grid-area: content;
  }
}
</style>
